import {
  theDomWatcher,
  handleNewsletterSubscribed,
  trackNewsletterSignup,
  validateNewsletterEmail,
  initProductDescriptions,
} from "@theme/helpers";

import { ThemeMotion } from "@theme/theme-motion";
new ThemeMotion().init();

const featureLoaders = [
  {
    label: "cart",
    selectors: ["[data-cart-drawer]", "[data-cart-container]", "[data-open-cart]"],
    observe: true,
    loader: () => import("@theme/cart"),
  },
  {
    label: "wishlist",
    selectors: ["[wishlist-button]", "[wishlist-grid]", "[side-cart-wishlist-grid]"],
    observe: true,
    loader: () => import("@theme/wishlist"),
  },
  {
    label: "side-cart-tabs",
    selectors: ['.side-cart-tabs[role="tablist"]', "[data-cart-tab]"],
    observe: true,
    loader: () => import("@theme/tabs"),
  },
  {
    label: "toasty",
    selectors: ["body"],
    loader: () => import("@theme/toasty"),
  },
  {
    label: "product-quick-add",
    selectors: ["[quick-add-qf]", "product-form"],
    observe: true,
    loader: () =>
      import("@theme/product-quick-add").then(({ ProductQuickAdd }) => new ProductQuickAdd()),
  },
  {
    label: "quickview",
    selectors: ["product-quick-view", "quickview-modal"],
    loader: () => import("@theme/quickview").then(({ ProductQuickView }) => new ProductQuickView()),
  },
  {
    label: "main-product",
    selectors: ["main-product"],
    observe: true,
    loader: () => import("@theme/main-product"),
  },
  {
    label: "product-configurator",
    selectors: ["product-configurator"],
    observe: true,
    loader: () => import("@theme/product-configurator"),
  },
  {
    label: "request-a-quote",
    selectors: ["request-a-quote"],
    observe: true,
    loader: () => import("@theme/request-a-quote"),
  },
  {
    label: "product-gallery-grid",
    selectors: ['[gallery-container][data-gallery-style="grid"]'],
    loader: () => import("@theme/product-gallery"),
  },
  // {
  //   label: "product-form",
  //   selectors: ["product-form"],
  //   observe: true,
  //   loader: () => import("@theme/option-selectors").then(({ ProductForm }) => new ProductForm()),
  // },
  {
    label: "product-card",
    selectors: ["product-card"],
    observe: true,
    loader: () => import("@theme/product-card").then(({ ProductCard }) => new ProductCard()),
  },
  {
    label: "custom-option-dropdown",
    selectors: ["custom-option-dropdown"],
    observe: true,
    loader: () => import("@theme/custom-option-dropdown"),
  },
  {
    label: "custom-printing-field",
    selectors: ["custom-printing-field"],
    observe: true,
    loader: () => import("@theme/custom-printing-field"),
  },
  {
    label: "product-upsell",
    selectors: ["product-upsell"],
    observe: true,
    loader: () => import("@theme/product-upsell"),
  },
  {
    label: "predictive-search",
    selectors: ["predictive-search"],
    loader: () => import("@theme/search").then(({ SearchController }) => new SearchController()),
  },
  {
    label: "menu",
    selectors: ["#open-mobile-menu", ".mobile-menu-outer-wrapper", ".top-level-links"],
    loader: () => import("@theme/menu").then(({ MenuController }) => new MenuController()),
  },
  {
    label: "accordion",
    selectors: [".accordion"],
    loader: () =>
      import("@theme/accordion").then(({ AccordionController }) => {
        if (window.__quickfireAccordionController) {
          return window.__quickfireAccordionController;
        }
        const instance = new AccordionController();
        window.__quickfireAccordionController = instance;
        return instance;
      }),
  },
  {
    label: "product-quantity",
    selectors: ["[quantity-select]"],
    observe: true,
    loader: () =>
      import("@theme/product-quantity").then(({ ProductQuantity }) => new ProductQuantity()),
  },
  {
    label: "header-layout",
    selectors: ["[data-header-layout]"],
    loader: () =>
      import("@theme/header-layout").then(
        ({ HeaderLayoutController }) => new HeaderLayoutController(),
      ),
  },
  {
    label: "header-scroll",
    selectors: [".header-outer-shopify-wrapper"],
    loader: () =>
      import("@theme/header-scroll").then(
        ({ HeaderScrollController }) => new HeaderScrollController(),
      ),
  },
  {
    label: "header-transparency",
    selectors: [
      (root) =>
        root.querySelector(".header-outer-shopify-wrapper") &&
        root.querySelector("[data-transparent-header]"),
    ],
    observe: true,
    loader: () =>
      import("@theme/header-transparency").then(
        ({ HeaderTransparencyController }) => new HeaderTransparencyController(),
      ),
  },
  {
    label: "modal-controller",
    selectors: ["[data-modal-trigger]", "[data-modal]"],
    loader: () =>
      import("@theme/modal-controller").then(({ ModalController }) => {
        if (window.__quickfireModalController) {
          return window.__quickfireModalController;
        }
        const instance = new ModalController();
        window.__quickfireModalController = instance;
        return instance;
      }),
  },
  {
    label: "video-controls",
    selectors: ["[js-video-controls]"],
    observe: true,
    loader: () => import("@theme/video-controls"),
  },
  {
    label: "lightbox",
    selectors: ["[data-lightbox-modal]"],
    observe: true,
    loader: () =>
      import("@theme/lightbox").then(({ bootstrapLightbox }) => bootstrapLightbox()),
  },
  {
    label: "column-scroll-highlight",
    selectors: ["[data-column-highlight]"],
    observe: true,
    loader: () =>
      import("@theme/column-scroll-highlight")
        .then(({ ColumnScrollHighlightController }) => new ColumnScrollHighlightController())
        .catch((error) => {
          document
            .querySelectorAll("[data-column-highlight]")
            .forEach((column) => column.classList.add("is-revealed"));
          throw error;
        }),
  },
];

featureLoaders.forEach(({ selectors, loader, label, observe }) =>
  theDomWatcher(selectors, loader, { label, observe }),
);

theDomWatcher(
  "[data-blaze-slider]",
  () =>
    import("@theme/blaze-slider-controller").then(({ blazeSliderController }) => {
      blazeSliderController.init(document);
      return blazeSliderController;
    }),
  { label: "blaze-slider", observe: true },
);

theDomWatcher("form.newsletter-form", handleNewsletterSubscribed, { label: "newsletter-form" });
theDomWatcher("form.newsletter-form", validateNewsletterEmail, { label: "newsletter-validation" });
theDomWatcher('input[name="contact[email]"]', trackNewsletterSignup, {
  label: "newsletter-signup",
});
theDomWatcher("[read-more-container]", initProductDescriptions, { label: "read-more" });
